import { Trans } from 'react-i18next';

import {
   HealthcareProviderAppointmentCancellationTokenInReminderEnabledQuery,
   HealthcareProviderAppointmentReminderPatientNotificationEnabledSetCommand,
} from '@dreryk/egabinet-phone-notification-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { useFormController } from '@dreryk/react-common';
import {
   dispatchSnackbarPositiveEvent,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   Toggle,
   Tooltip,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { PatientPhoneNotificationAppointmentReminderToggleLoader } from './PatientPhoneNotificationAppointmentReminderToggle.loader';
import { PhoneNotificationTooltipText } from './PhoneNotificationTooltipText';

const SAVED_SNACKBAR_DELAY_IN_MS = 5000;
const useCurrentHealthcareProviderAppointmentReminderPatientNotificationEnabledSetCommand = (
   onSuccess: VoidFunction
): UseCqrsCommandResult<void, PatientPhoneNotificationAppointmentReminderToggleFormBean> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new HealthcareProviderAppointmentReminderPatientNotificationEnabledSetCommand();
         command.enabled = Choice.of(parameters.notificationEnabled ?? false);
         return command;
      },
      {
         onSuccess: (ignore, parameters) => {
            dispatchSnackbarPositiveEvent(
               <>
                  <Trans
                     i18nKey={
                        parameters.notificationEnabled
                           ? patientPhoneNotificationTranslationKeys.notificationToggle.appointmentReminderToggleSaveOn
                           : patientPhoneNotificationTranslationKeys.notificationToggle.appointmentReminderToggleSaveOff
                     }
                  />
               </>,
               {
                  autoHideDelayInMs: SAVED_SNACKBAR_DELAY_IN_MS,
               }
            );
            onSuccess();
            refetchCqrsQuery(HealthcareProviderAppointmentCancellationTokenInReminderEnabledQuery);
         },
      }
   );
};

type PatientPhoneNotificationAppointmentReminderToggleFormBean = {
   notificationEnabled: boolean;
};

export const PatientPhoneNotificationAppointmentReminderToggle = (props: {
   readonly notificationEnabled: boolean;
   readonly messages: string[];
   readonly refetch: VoidFunction;
}): JSX.Element | null => {
   const { register, handleSubmit } = useFormController<PatientPhoneNotificationAppointmentReminderToggleFormBean>(
      {
         notificationEnabled: props.notificationEnabled,
      },
      { mode: 'onChange' }
   );
   const {
      executeAsync: onSubmit,
      loading,
   } = useCurrentHealthcareProviderAppointmentReminderPatientNotificationEnabledSetCommand(props.refetch);

   return (
      <StyledFlex gap={'12px'}>
         <form onInput={handleSubmit(onSubmit)}>
            <Toggle
               controller={register('notificationEnabled')}
               testId={'patientPhoneNotificationAppointmentReminderToggle'}
               disabled={loading}
            />
         </form>
         <StyledFlex gap={'4px'} alignItems={'center'}>
            <Trans i18nKey={patientPhoneNotificationTranslationKeys.notificationToggle.appointmentReminderToggle} />
            <Tooltip
               maxWidth={'500px'}
               testId={'appointmentReminderToggleTooltip'}
               content={
                  <PhoneNotificationTooltipText
                     header={
                        patientPhoneNotificationTranslationKeys.notificationToggle.appointmentReminderToggleTooltip
                     }
                     messages={props.messages}
                  />
               }
            >
               <IconWrapper
                  testId={'appointmentReminderToggleTooltipIconWrapper'}
                  fillColor={IconFillColor.PRIMARY}
                  size={IconSize.SMALL}
               >
                  <QuestionmarkCircleIcon />
               </IconWrapper>
            </Tooltip>
         </StyledFlex>
      </StyledFlex>
   );
};

export const PatientPhoneNotificationAppointmentReminderToggleWithLoader = (): JSX.Element => {
   return (
      <PatientPhoneNotificationAppointmentReminderToggleLoader>
         {({ notificationEnabled, messages, refetch }) => (
            <PatientPhoneNotificationAppointmentReminderToggle
               notificationEnabled={notificationEnabled}
               messages={messages}
               refetch={refetch}
            />
         )}
      </PatientPhoneNotificationAppointmentReminderToggleLoader>
   );
};
